
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'

import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { ItemSubType } from '../../types/models'
import { plural } from '../../utils/item'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { ItemPage } from './ItemPage'

type Props = {
    action: 'new' | 'edit' | 'copy'
    type: ItemSubType
}


const viewWillAppear = async ({ urlParams, props: { action, type } }: PocketComponent<Props>) => {
    const item = await (async (): Promise<Item | null> => {
        switch (action) {
            case 'new': {
                return new Item({
                    type,
                    addon_all: true,
                    is_public: true,
                    capacity_type: 'session',
                })
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                return (await RecordFactory.getInstance().loadItem(urlParams.id)) ?? null
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const packageItem =
                    (await RecordFactory.getInstance().loadItem(urlParams.id)) ?? null
                if (packageItem === null) {
                    return null
                }
                return packageItem.clone({ id: undefined, name: undefined })
            }
        }
    })()
    if (!item) {
        return new RecordNotFoundError()
    }

    return {
        item,
        action,
    }
}

const render = ({
    data,
    base: {
        logAndDisplayError,
        props: { type },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { item } = data

    const navigate = useNavigate()

    const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] = useState(false)

    const { findLastHistoryEntry } = useHistoryTrackerContext()

    const onClose = useCallback(() => {
        const lastHistoryEntry = findLastHistoryEntry({
            excluding: new RegExp(`/settings/${plural(type)}/.+`),
        })
        if (lastHistoryEntry) {
            navigate(lastHistoryEntry.pathname)
        }
        else {
            navigate(`/settings/${plural(type)}`)
        }
    }, [findLastHistoryEntry, navigate, type])

    const handleSave = async (packageItem: Item, photo?: File) => {
        const result = await RecordFactory.getInstance().sendItem(packageItem, photo)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        onClose()
    }

    const handleDelete = async () => {
        const itemID = item.id
        if (itemID === undefined) {
            return
        }

        const result = await RecordFactory.getInstance().deleteItem(itemID, false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        onClose()
    }

    return (
        <>
            <ItemPage
                itemType={type}
                item={item}
                onUpdateItem={handleSave}
                onClose={onClose}
                onDelete={
                    data.action === 'new' ? undefined : () => setIsShowingDeleteConfirmation(true)
                }
                enableAllFields={true}
            />

            <ConfirmationModal
                isOpen={isShowingDeleteConfirmation}
                onClose={() => setIsShowingDeleteConfirmation(false)}
                title={`Delete ${item.name}?`}
                size="small"
                confirmButtonText="Yes, delete"
                isDestructive
                shouldHideCancelButton
                onConfirm={handleDelete}
            >
                <Paragraph>This action cannot be undone.</Paragraph>
            </ConfirmationModal>
        </>
    )
}

export const ItemWorkflow = usePSComponent(render, viewWillAppear)
